import Grid from "@mui/material/Grid";
import WorkItemSearchFieldWorkItem from "../../fields/WorkItem";
import WorkItemSearchFieldStatus from "../../fields/WorkItemSearchFieldStatus";
import WorkItemSearchFieldWorkspace from "../../fields/WorkItemSearchFieldWorkspace";
import WorkItemSearchFieldResource from "../../fields/WorkItemSearchFieldResource";
import WorkItemSearchFieldContentType from "../../fields/WorkItemSearchFieldContentType";
import Button from "@mui/material/Button";
import { SetStateAction, useState } from "react";
import useWorkItemAutocompleteResource, { GetAutocompleteResult } from "hooks/work-items/useWorkItemAutocompleteResource";
import useWorkItemListOfValues from "hooks/work-items/useWorkItemListOfValues";
import useWorkItemPossibleStatus from "hooks/work-items/useWorkItemPossibleStatus";
import { WorkItemSearchCriteria } from "hooks/work-items/useWorkItem";
import WorkItemSearchFieldResponsibility from "../../fields/WorkItemSearchFieldResponsibility";
import { BulkUpdateModes } from "../../WorkItemBulkUpdate";
import { Box } from "@mui/material";
import { Ico_ArrowDown, Ico_ArrowUp } from "@sagecentral/core-ui";
import { IconButton } from '@mui/material';

interface IBulkUpdateSearchResources {
    cancelButtonText?: string | undefined,
    onCancel: () => void,
    onSearch: (payload: WorkItemSearchCriteria) => void
}

const BulkUpdateSearchResources = (props: IBulkUpdateSearchResources) => {

    const [showingAdvancedSearch, setShowingAdvancedSearch] = useState<boolean>(false);
    const [resource, setResource] = useState('');
    const [userId, setUserId] = useState('');
    const [workItem, setWorkItem] = useState('');
    const [workspaces, setWorkspaces] = useState<number[]>([]);
    const [workItemStatuses, setWorkItemStatuses] = useState<number[]>([]);
    const { WorkItemSearchStatuses, WorkItemStatusLoading, WorkItemResourcesSearchStatuses } = useWorkItemPossibleStatus();
    const [contentTypes, setContentTypes] = useState<number[]>([]);
    const [useAndOperator, setUseAndOperator] = useState<boolean>(false);
    const [onlyAsPrimaryResource, setOnlyAsPrimaryResource] = useState<boolean>(true);
    const [workItemResponsibilities, setWorkItemResponsibilities] = useState<number[]>([]);
    const [resources, loadingResources, errorResources, setResources] = useWorkItemAutocompleteResource(resource);
    const [isPrimaryResource, setIsPrimaryResource] = useState<boolean>(false);
    const [workitemLOVs, loadingWorkItemLOVs, errorWorkItemLOVs] = useWorkItemListOfValues();

    const onChangeResource = (e: any, newValue: string | GetAutocompleteResult | null) => {
        if (typeof newValue === 'string' || newValue === null) {
            setUserId(newValue ? newValue as string : '');
        }
        else {
            setUserId(newValue.userId)
        }
        setResources(null);
    }

    const handleIsPrimaryResource = (isChecked: boolean) => {
        setIsPrimaryResource(isChecked);

    }

    const handleSearch = () => {
        var searchPayload: WorkItemSearchCriteria = {
            contentType: contentTypes,
            resource: userId ?? '',
            status: workItemStatuses,
            workItemSearchText: workItem,
            workspaces: workspaces,
            resourceOnlyAsPrimary: onlyAsPrimaryResource,
            includeChildWorkItems: false,
            searchAll: true,
            userId: userId ?? '',
            responsibilities: workItemResponsibilities,
            responsibilityUseOrOperator: !useAndOperator,
            matchOnPrimary: isPrimaryResource,
        };
        searchPayload.hash = GetHashCode(`${resource}${isPrimaryResource}${JSON.stringify(workItemResponsibilities)}`);
        props.onSearch(searchPayload);
    }

    const GetHashCode = (str: string): number => {
        var hash = 0,
            i, chr;
        if (str.length === 0) return hash;
        for (i = 0; i < str.length; i++) {
            chr = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + chr;
            hash |= 0; // Convert to 32bit integer
        }
        return hash;
    }

    const ClearAdvancedSearch = () => {
        setWorkItem('');
        setWorkspaces([]);
        setContentTypes([]);
        setWorkItemStatuses([]);
    }

    const toggleAdvancedSearch = () => {
        ClearAdvancedSearch();
        setShowingAdvancedSearch(!showingAdvancedSearch);
    }

    return (
        <>

            <Grid xs={4} sm={4} md={4} lg={4} mb='50px'>
                <Box mb='10px'>
                    <WorkItemSearchFieldResource
                        idSuffix='Resources'
                        label="Search by Resource"
                        labelProps={{ fontWeight: 'bold', color: '#000000' }}
                        onChange={onChangeResource}
                        onChangeCheckbox={() => setOnlyAsPrimaryResource(!onlyAsPrimaryResource)}
                        loadingResources={loadingResources}
                        errorResources={errorResources}
                        onKeyUp={e => setResource(e.target.value)}
                        resources={resources}
                        mode={BulkUpdateModes.Status}
                    />
                </Box>

                <Box mb='10px'>
                    <WorkItemSearchFieldResponsibility
                        idSuffix='Resources'
                        label="Search by Responsibility"
                        labelProps={{ fontWeight: 'bold', color: '#000000' }}
                        options={workitemLOVs?.responsibilities}
                        onChange={responsibilities => setWorkItemResponsibilities(responsibilities)}
                        isPrimaryChecked={isPrimaryResource}
                        onChangeIsPrimaryCheckbox={(checked: boolean) => handleIsPrimaryResource(checked)}
                        isMatchAllResponsibilitlesChecked={useAndOperator}
                        onChangeIsMatchAllResponsibilitlesChecked={() => setUseAndOperator(!useAndOperator)} />
                </Box>

                <Box mb='10px' display="flex" justifyContent="start" alignItems="center">
                    <IconButton onClick={toggleAdvancedSearch}>
                        {showingAdvancedSearch
                            ? <Ico_ArrowUp color='info' />
                            : <Ico_ArrowDown color='primary' />}
                    </IconButton>
                    &nbsp;Advanced Search
                </Box>

                {showingAdvancedSearch &&
                    <Box margin='10px'>
                        <WorkItemSearchFieldWorkItem
                            idSuffix='Resources'
                            labelProps={{ fontWeight: 'bold', color: '#000000' }}
                            onChange={e => setWorkItem(e.target.value)}
                            canIncludeChilren={false}
                        />
                    </Box>}

                {showingAdvancedSearch &&
                    <Box margin='10px'>
                        <WorkItemSearchFieldWorkspace
                            idSuffix='Resources'
                            labelProps={{ fontWeight: 'bold', color: '#000000' }}
                            options={workitemLOVs?.workSpaces}
                            onChange={workspaces => setWorkspaces(workspaces)}
                        />
                    </Box>}

                {showingAdvancedSearch &&
                    <Box margin='10px'>
                        <WorkItemSearchFieldContentType
                            idSuffix='Resources'
                            labelProps={{ fontWeight: 'bold', color: '#000000' }}
                            options={workitemLOVs?.contentTypes}
                            onChange={(contentTypes: SetStateAction<number[]>) => setContentTypes(contentTypes)} />
                    </Box>}

                {showingAdvancedSearch &&
                    <Box margin='10px'>
                        <WorkItemSearchFieldStatus
                            idSuffix='Resources'
                            label="Work Item Status"
                            labelProps={{ fontWeight: 'bold', color: '#000000' }}
                            onChange={statuses => setWorkItemStatuses(statuses)}
                            options={WorkItemResourcesSearchStatuses} />
                    </Box>}

                <Grid xs={12} sm={12} md={12} lg={12}
                    mt='30px'
                    flexDirection='row' container>
                    <Grid item lg={6} >
                        <Button
                            id={"bu-clear-grid-resources"}
                            sx={{ width: '100%', ml: '0px' }}
                            variant="outlined"
                            onClick={props.onCancel}>{props.cancelButtonText || 'Clear Grid'}</Button>
                    </Grid>
                    <Grid item lg={6} >
                        <Button
                            id={"bu-search-resources"}
                            sx={{ width: '100%' }}
                            variant="contained"
                            onClick={handleSearch}>Search</Button>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
}

export default BulkUpdateSearchResources;